import { initializeApp } from "firebase/app";
import {
  getAuth as getFirebaseAuth,
  initializeAuth,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  inMemoryPersistence,
  browserPopupRedirectResolver,
} from "firebase/auth";
import { getMessaging, isSupported } from "firebase/messaging";
import {
  getStorage as getFirebaseStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
} from "firebase/storage";
import { GoogleAuthProvider } from "firebase/auth";

export const firebaseConfig = JSON.parse(process.env.REACT_APP_FIREBASE_CONFIG || "");

let app;
let auth;
let storage;
let messaging;

// Initialize Firebase if it hasn't been initialized yet
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  if (!/already exists/.test(error.message)) {
    console.error("Firebase initialization error", error.stack);
  }
}

// Get Auth instance.
//
// Some browsers (notably Chrome with a corrupted profile, restricted storage,
// or certain incognito/extension states) fail to open IndexedDB, throwing
// "UnknownError: Internal error opening backing store for indexedDB.open."
// Firebase Auth defaults to IndexedDB persistence, so that failure surfaces as
// an unhandled rejection and breaks anonymous sign-in on the public survey page.
//
// Passing an explicit persistence chain lets Firebase fall back through
// localStorage and sessionStorage to in-memory persistence, so auth still works
// (it just won't survive a reload) instead of crashing.
export const getAuthInstance = () => {
  if (!auth) {
    try {
      auth = initializeAuth(app, {
        persistence: [
          indexedDBLocalPersistence,
          browserLocalPersistence,
          browserSessionPersistence,
          inMemoryPersistence,
        ],
        // initializeAuth does not auto-register the popup/redirect resolver the
        // way getAuth() does. Without it, signInWithPopup throws
        // auth/argument-error, breaking Google sign-in.
        popupRedirectResolver: browserPopupRedirectResolver,
      });
    } catch (error) {
      // initializeAuth throws if auth was already initialized for this app
      // (e.g. hot reload or duplicate module instances). Reuse the existing one.
      auth = getFirebaseAuth(app);
    }
  }
  return auth;
};

// Get Storage instance
export const getStorageInstance = () => {
  if (!storage) {
    storage = getFirebaseStorage(app);
  }
  return storage;
};

export const getMessagingInstance = async () => {
  if (!messaging) {
    const supported = await isSupported();
    if (supported) {
      messaging = getMessaging(app);
    } else {
      console.warn('Firebase Messaging is not supported in this browser');
      return null;
    }
  }
  return messaging;
};

// Export storage related functions
export { ref, uploadBytes, getDownloadURL, deleteObject, listAll };

// Export the app instance
export { app };

const provider = new GoogleAuthProvider();
provider.addScope("profile");
provider.addScope("email");
